import { addDays, isIsoDate } from './sales-dates.js';
export const SALES_RANGE_PRESETS = [
    'today',
    'yesterday',
    'last_7d',
    'last_30d',
    'last_90d',
    'mtd',
    'last_month',
    'qtd',
    'last_quarter',
    'ytd',
    'last_year',
    'last_12m',
    'custom',
];
export const SALES_RANGE_LABELS = {
    today: 'Today',
    yesterday: 'Yesterday',
    last_7d: 'Last 7 days',
    last_30d: 'Last 30 days',
    last_90d: 'Last 90 days',
    mtd: 'Month to date',
    last_month: 'Last month',
    qtd: 'Quarter to date',
    last_quarter: 'Last quarter',
    ytd: 'Year to date',
    last_year: 'Last year',
    last_12m: 'Last 12 months',
    custom: 'Custom',
};
export const SALES_COMPARE_MODES = ['none', 'previous_period', 'previous_year'];
export const SALES_GRANULARITIES = ['day', 'week', 'month', 'quarter'];
// ---- calendar helpers -------------------------------------------------------
const parts = (date) => {
    if (!isIsoDate(date))
        throw new Error(`Not a calendar date: ${date}`);
    return [Number(date.slice(0, 4)), Number(date.slice(5, 7)), Number(date.slice(8, 10))];
};
const format = (y, m, d) => `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const daysInMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
/** The date `months` calendar months away, with the day clamped to that month's length (Mar 31 − 1 month = Feb 28). */
export function shiftMonths(date, months) {
    const [y, m, d] = parts(date);
    const index = y * 12 + (m - 1) + months;
    const ny = Math.floor(index / 12);
    const nm = (index % 12) + 1;
    return format(ny, nm, Math.min(d, daysInMonth(ny, nm)));
}
export const startOfMonth = (date) => date.slice(0, 8) + '01';
export function endOfMonth(date) {
    const [y, m] = parts(date);
    return format(y, m, daysInMonth(y, m));
}
export function startOfQuarter(date) {
    const [y, m] = parts(date);
    return format(y, Math.floor((m - 1) / 3) * 3 + 1, 1);
}
export const startOfYear = (date) => date.slice(0, 4) + '-01-01';
/** Monday of the ISO week containing `date`. */
export function startOfWeek(date) {
    const [y, m, d] = parts(date);
    const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
    return addDays(date, -((weekday + 6) % 7));
}
/** 0 = Monday … 6 = Sunday. */
export function isoWeekday(date) {
    const [y, m, d] = parts(date);
    return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}
/** Whole days from `from` to `to`, counting both. */
export function daysInclusive(range) {
    const [fy, fm, fd] = parts(range.from);
    const [ty, tm, td] = parts(range.to);
    return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000) + 1;
}
const isWholeMonths = (range) => range.from === startOfMonth(range.from) && range.to === endOfMonth(range.to);
const monthsSpanned = (range) => {
    const [fy, fm] = parts(range.from);
    const [ty, tm] = parts(range.to);
    return (ty - fy) * 12 + (tm - fm) + 1;
};
// ---- ranges -----------------------------------------------------------------
/**
 * The dates a preset covers on `today`. Rolling "last N days" presets end
 * yesterday: comparing a half-finished today against a whole day a week ago
 * would always read as a decline. The to-date presets do include today, as
 * everyone expects of "month to date".
 */
export function resolveRange(preset, today, custom) {
    switch (preset) {
        case 'today':
            return { from: today, to: today };
        case 'yesterday':
            return { from: addDays(today, -1), to: addDays(today, -1) };
        case 'last_7d':
            return { from: addDays(today, -7), to: addDays(today, -1) };
        case 'last_30d':
            return { from: addDays(today, -30), to: addDays(today, -1) };
        case 'last_90d':
            return { from: addDays(today, -90), to: addDays(today, -1) };
        case 'mtd':
            return { from: startOfMonth(today), to: today };
        case 'last_month': {
            const from = shiftMonths(startOfMonth(today), -1);
            return { from, to: endOfMonth(from) };
        }
        case 'qtd':
            return { from: startOfQuarter(today), to: today };
        case 'last_quarter': {
            const from = shiftMonths(startOfQuarter(today), -3);
            return { from, to: addDays(startOfQuarter(today), -1) };
        }
        case 'ytd':
            return { from: startOfYear(today), to: today };
        case 'last_year': {
            const year = Number(today.slice(0, 4)) - 1;
            return { from: `${year}-01-01`, to: `${year}-12-31` };
        }
        case 'last_12m':
            // Twelve calendar months including this one, so a monthly chart has no
            // ragged first bar.
            return { from: shiftMonths(startOfMonth(today), -11), to: today };
        case 'custom': {
            const from = custom?.from && isIsoDate(custom.from) ? custom.from : addDays(today, -30);
            const to = custom?.to && isIsoDate(custom.to) ? custom.to : today;
            return from <= to ? { from, to } : { from: to, to: from };
        }
    }
}
/** Presets that run from the start of a calendar period to today, and how long that period is in months. */
const TO_DATE_MONTHS = { mtd: 1, qtd: 3, ytd: 12 };
/**
 * The period a range is compared against, or null for no comparison.
 *
 *   * previous_period — month to date compares with the same days of last
 *     month (Sep 1–24 against Aug 1–24), a whole month or quarter with the one
 *     before it, and anything else with the same number of days just before.
 *   * previous_year — the same dates a year earlier, Feb 29 falling back to
 *     Feb 28; with `alignWeekdays`, exactly 52 weeks earlier instead, so
 *     Saturdays line up with Saturdays.
 */
export function compareRange(range, mode, options = {}) {
    if (mode === 'none')
        return null;
    if (mode === 'previous_year') {
        if (options.alignWeekdays)
            return { from: addDays(range.from, -364), to: addDays(range.to, -364) };
        if (isWholeMonths(range)) {
            const from = shiftMonths(range.from, -12);
            return { from, to: endOfMonth(shiftMonths(range.to, -12)) };
        }
        return { from: shiftMonths(range.from, -12), to: shiftMonths(range.to, -12) };
    }
    const toDate = options.preset ? TO_DATE_MONTHS[options.preset] : undefined;
    if (toDate)
        return { from: shiftMonths(range.from, -toDate), to: shiftMonths(range.to, -toDate) };
    if (isWholeMonths(range)) {
        const months = monthsSpanned(range);
        const from = shiftMonths(range.from, -months);
        return { from, to: addDays(range.from, -1) };
    }
    const length = daysInclusive(range);
    return { from: addDays(range.from, -length), to: addDays(range.from, -1) };
}
// ---- buckets ----------------------------------------------------------------
/** A sensible chart grain for a range: enough points to show a shape, few enough to read. */
export function autoGranularity(range) {
    const days = daysInclusive(range);
    if (days <= 62)
        return 'day';
    if (days <= 190)
        return 'week';
    if (days <= 1100)
        return 'month';
    return 'quarter';
}
export function bucketStart(date, granularity) {
    switch (granularity) {
        case 'day':
            return date;
        case 'week':
            return startOfWeek(date);
        case 'month':
            return startOfMonth(date);
        case 'quarter':
            return startOfQuarter(date);
    }
}
export function nextBucket(start, granularity) {
    switch (granularity) {
        case 'day':
            return addDays(start, 1);
        case 'week':
            return addDays(start, 7);
        case 'month':
            return shiftMonths(start, 1);
        case 'quarter':
            return shiftMonths(start, 3);
    }
}
/** Every bucket touching the range, in order — the x axis, so empty periods still show as zero. */
export function bucketsBetween(range, granularity) {
    const buckets = [];
    let cursor = bucketStart(range.from, granularity);
    // Bounded so a malformed range can never spin; 20 years of days is well past any real request.
    while (cursor <= range.to && buckets.length < 7400) {
        buckets.push(cursor);
        cursor = nextBucket(cursor, granularity);
    }
    return buckets;
}
//# sourceMappingURL=sales-periods.js.map